/**
 * PB-09 — public holidays by country (EE, FI, LV, LT), browser and server.
 *
 * The rules below are the statutory days off as of 2026. They are the
 * computed default for the WORKER's payroll country (default EE) — never
 * derived from a site's zone or country: temporary work abroad changes
 * neither the worker's norm nor their pay calendar, and a client's holiday
 * pricing comes from the order's price agreement (Marti 2026-10-22). They are
 * the computed default only: an admin-curated `/workingTimeNorms/{country}_{year}`
 * document still wins for working-time norms, and a country's one-off days
 * (e.g. a moved holiday decreed for a single year) belong there.
 */
import type { CountryCode } from './client';

function easterSunday(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(Date.UTC(year, month - 1, day));
}

const ymd = (d: Date) => d.toISOString().slice(0, 10);
const plus = (d: Date, days: number) => new Date(d.getTime() + days * 86_400_000);
const date = (year: number, month: number, day: number) => new Date(Date.UTC(year, month - 1, day));

/** The first `weekday` (0 = Sunday … 6 = Saturday) on or after a date. */
function firstWeekdayFrom(from: Date, weekday: number): Date {
  return plus(from, (weekday - from.getUTCDay() + 7) % 7);
}

/** A date that falls on a weekend moves to the next Monday (Latvia). */
function nextMondayIfWeekend(d: Date): Date {
  const dow = d.getUTCDay();
  return dow === 6 ? plus(d, 2) : dow === 0 ? plus(d, 1) : d;
}

/** YYYY-MM-DD of every public holiday (day off) in that country's year. */
export function publicHolidayDates(country: CountryCode, year: number): Set<string> {
  const easter = easterSunday(year);
  const days: Date[] = [];
  switch (country) {
    case 'EE':
      days.push(
        date(year, 1, 1),
        date(year, 2, 24),
        plus(easter, -2),
        easter,
        date(year, 5, 1),
        plus(easter, 49),
        date(year, 6, 23),
        date(year, 6, 24),
        date(year, 8, 20),
        date(year, 12, 24),
        date(year, 12, 25),
        date(year, 12, 26),
      );
      break;
    case 'FI':
      days.push(
        date(year, 1, 1),
        date(year, 1, 6),
        plus(easter, -2),
        easter,
        plus(easter, 1),
        date(year, 5, 1),
        plus(easter, 39), // Ascension Day
        plus(easter, 49), // Pentecost
        firstWeekdayFrom(date(year, 6, 19), 5), // Midsummer Eve (Fri 19–25 June)
        firstWeekdayFrom(date(year, 6, 20), 6), // Midsummer Day (Sat 20–26 June)
        firstWeekdayFrom(date(year, 10, 31), 6), // All Saints' Day (Sat 31 Oct – 6 Nov)
        date(year, 12, 6),
        date(year, 12, 24),
        date(year, 12, 25),
        date(year, 12, 26),
      );
      break;
    case 'LV':
      days.push(
        date(year, 1, 1),
        plus(easter, -2),
        easter,
        plus(easter, 1),
        date(year, 5, 1),
        nextMondayIfWeekend(date(year, 5, 4)),
        plus(easter, 49), // Pentecost
        date(year, 6, 23),
        date(year, 6, 24),
        nextMondayIfWeekend(date(year, 11, 18)),
        date(year, 12, 24),
        date(year, 12, 25),
        date(year, 12, 26),
        date(year, 12, 31),
      );
      break;
    case 'LT':
      days.push(
        date(year, 1, 1),
        date(year, 2, 16),
        date(year, 3, 11),
        easter,
        plus(easter, 1),
        date(year, 5, 1),
        firstWeekdayFrom(date(year, 5, 1), 0), // Mother's Day (first Sunday of May)
        firstWeekdayFrom(date(year, 6, 1), 0), // Father's Day (first Sunday of June)
        date(year, 6, 24),
        date(year, 7, 6),
        date(year, 8, 15),
        date(year, 11, 1),
        date(year, 11, 2),
        date(year, 12, 24),
        date(year, 12, 25),
        date(year, 12, 26),
      );
      break;
  }
  return new Set(days.map(ymd));
}
